import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { Job, JobItem, JobProgress } from '@/api/types';
import { useJobPolling } from '@/hooks/useJobPolling';
import {
  canCancelJob,
  canRetryJob,
  formatJobItemSource,
  formatJobType,
  isActiveJobStatus,
  isTerminalJobStatus,
  jobStatusLabel,
  jobStatusVariant,
  shortId,
} from '@/utils/jobStatus';
import { formatApiError } from '@/utils/formatApiError';
import { formatTableError } from '@/components/table/formatCellValue';
import { resolveJobRetryId } from '@/utils/jobRetryIds';
import StatusBadge from '@/components/ui/StatusBadge';
import { useToast } from '@/components/Toast';

const PROGRESS_POLL_MS = 2000;

type Props = {
  jobId: string;
  title?: string;
  onDismiss?: () => void;
  onTerminal?: (job: Job) => void;
  compact?: boolean;
  canManageActions?: boolean;
};

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function mergeProgressItems(progress: JobProgress | null, fallback: JobItem[]): JobItem[] {
  if (!progress?.items?.length) return fallback;
  return progress.items.map((row) => ({
    id: row.id,
    job_id: progress.job_id,
    type: 'scrape_source',
    status: row.status,
    payload: { source_name: row.source_name },
    item_key: row.item_key,
    attempt_count: row.attempt_count,
    max_attempts: row.max_attempts,
    lease_owner: row.lease_owner,
    lease_expires_at: null,
    created_at: row.started_at || '',
    started_at: row.started_at,
    finished_at: row.finished_at,
    error: row.error,
  }));
}

export default function JobProgressCard({
  jobId,
  title = 'Scrape job',
  onDismiss,
  onTerminal,
  compact = false,
  canManageActions = false,
}: Props) {
  const { toast } = useToast();
  const { job, error, loading, refresh } = useJobPolling(jobId, { onTerminal });
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [progressError, setProgressError] = useState('');
  const [itemsOpen, setItemsOpen] = useState(false);
  const [items, setItems] = useState<JobItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [actionPending, setActionPending] = useState<'cancel' | 'retry' | null>(null);

  const loadProgress = useCallback(async () => {
    try {
      const data = await api.jobs.getProgress(jobId);
      setProgress(data);
      setProgressError('');
      if (data.items.length) {
        setItems(mergeProgressItems(data, []));
      }
      return data;
    } catch (e) {
      setProgressError(formatApiError(e, 'Failed to load job progress'));
      return null;
    }
  }, [jobId]);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const rows = await api.jobs.listItems(jobId, { limit: 200 });
      setItems(rows);
    } catch (e) {
      toast(formatApiError(e, 'Failed to load job items'), 'error');
    } finally {
      setItemsLoading(false);
    }
  }, [jobId, toast]);

  useEffect(() => {
    if (!jobId || !job || !isActiveJobStatus(job.status)) {
      if (job && isTerminalJobStatus(job.status)) {
        void loadProgress();
      }
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      if (cancelled) return;
      await loadProgress();
      if (cancelled) return;
      timeoutId = setTimeout(poll, PROGRESS_POLL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [job, jobId, loadProgress]);

  useEffect(() => {
    if (itemsOpen) {
      void loadItems();
    }
  }, [itemsOpen, loadItems]);

  useEffect(() => {
    if (progress?.items.length && items.length === 0) {
      setItems(mergeProgressItems(progress, []));
    }
  }, [progress, items.length]);

  async function handleCancel() {
    if (!job || !canCancelJob(job.status)) return;
    setActionPending('cancel');
    try {
      await api.jobs.cancel(actionJobId);
      await Promise.all([refresh(), loadProgress()]);
      toast('Job cancelled', 'info');
    } catch (e) {
      toast(formatApiError(e, 'Cancel failed'), 'error');
    } finally {
      setActionPending(null);
    }
  }

  async function handleRetry() {
    if (!job || !canRetryJob(job.status)) return;
    setActionPending('retry');
    try {
      await api.jobs.retry(actionJobId);
      await Promise.all([refresh(), loadProgress()]);
      toast('Job requeued for retry', 'success');
    } catch (e) {
      toast(formatApiError(e, 'Retry failed'), 'error');
    } finally {
      setActionPending(null);
    }
  }

  const display = job;
  const actionJobId = resolveJobRetryId(display, jobId);
  const telemetry = progress?.telemetry;
  const progressLabel =
    display && display.total_items > 0
      ? Math.min(100, Math.max(0, display.progress_percent))
      : 0;
  const displayItems = items.length ? items : mergeProgressItems(progress, []);
  const bannerError = error || progressError;

  return (
    <section className="card section job-progress-card" aria-live="polite">
      <header className="job-progress-card__header">
        <div>
          <h3 className="job-progress-card__title">{title}</h3>
          {display && (
            <p className="muted job-progress-card__subtitle">
              {formatJobType(display.type)}
              {!compact && (
                <span className="job-progress-card__id" title={display.id}>
                  {' '}
                  · {shortId(display.id)}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="job-progress-card__header-actions">
          {display && (
            <StatusBadge variant={jobStatusVariant(display.status)}>
              {jobStatusLabel(display.status)}
            </StatusBadge>
          )}
          {onDismiss && display && isTerminalJobStatus(display.status) && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onDismiss}>
              Dismiss
            </button>
          )}
        </div>
      </header>

      {loading && !display && (
        <div className="loading-inline" role="status">
          <span className="spinner" aria-hidden />
          Loading job…
        </div>
      )}

      {bannerError ? <p className="error-text job-progress-card__error">{bannerError}</p> : null}

      {display && (
        <>
          <div className="job-progress-card__progress">
            <div
              className="job-progress-bar"
              role="progressbar"
              aria-valuenow={progressLabel}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="job-progress-bar__fill" style={{ width: `${progressLabel}%` }} />
            </div>
            <p className="job-progress-card__counts">
              Progress: {display.processed_items} / {display.total_items}
              {display.failed_items > 0 && (
                <span className="job-progress-card__failed"> · {display.failed_items} failed</span>
              )}
            </p>
          </div>

          {telemetry && !compact ? (
            <div className="metric-chips job-progress-card__telemetry">
              <span className="metric-chip">Discovered: {telemetry.discovered_links}</span>
              <span className="metric-chip metric-chip--success">New: {telemetry.new_links}</span>
              <span className="metric-chip">Duplicates: {telemetry.duplicate_links}</span>
              {telemetry.rejected_links > 0 ? (
                <span className="metric-chip metric-chip--warning">
                  Rejected: {telemetry.rejected_links}
                </span>
              ) : null}
              <span className="metric-chip metric-chip--success">Sent: {telemetry.deliveries_sent}</span>
              {telemetry.deliveries_failed > 0 ? (
                <span className="metric-chip metric-chip--danger">Failed: {telemetry.deliveries_failed}</span>
              ) : null}
              {telemetry.deliveries_skipped > 0 ? (
                <span className="metric-chip metric-chip--warning">
                  Skipped: {telemetry.deliveries_skipped}
                </span>
              ) : null}
            </div>
          ) : null}

          {progress?.latest_activity && !compact ? (
            <p className="muted job-progress-card__latest-activity">
              Latest: {progress.latest_activity.message}
              {' · '}
              {formatTime(progress.latest_activity.created_at)}
            </p>
          ) : null}

          {!compact && isActiveJobStatus(display.status) && displayItems.length > 0 ? (
            <div className="table-scroll job-progress-card__items-preview">
              <table className="data-table job-items-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Attempts</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item) => (
                    <tr key={item.id}>
                      <td title={item.item_key || item.id}>{formatJobItemSource(item)}</td>
                      <td>
                        <StatusBadge variant={jobStatusVariant(item.status)}>
                          {jobStatusLabel(item.status)}
                        </StatusBadge>
                      </td>
                      <td>
                        {item.attempt_count}/{item.max_attempts}
                      </td>
                      <td>{formatTableError(item.error)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!compact && (
            <dl className="job-progress-card__meta">
              <div>
                <dt>Created</dt>
                <dd>{formatTime(display.created_at)}</dd>
              </div>
              <div>
                <dt>Started</dt>
                <dd>{formatTime(display.started_at)}</dd>
              </div>
              <div>
                <dt>Finished</dt>
                <dd>{formatTime(display.finished_at)}</dd>
              </div>
            </dl>
          )}

          {display.error && (
            <p className="job-progress-card__error" role="alert">
              {display.error}
            </p>
          )}

          <div className="job-progress-card__actions">
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                void Promise.all([refresh(), loadProgress()]);
              }}
            >
              Refresh
            </button>
            {canManageActions && canCancelJob(display.status) && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={actionPending !== null}
                onClick={handleCancel}
              >
                {actionPending === 'cancel' ? 'Cancelling…' : 'Cancel'}
              </button>
            )}
            {canManageActions && canRetryJob(display.status) && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={actionPending !== null}
                onClick={handleRetry}
              >
                {actionPending === 'retry' ? 'Retrying…' : 'Retry'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              aria-expanded={itemsOpen}
              onClick={() => setItemsOpen((v) => !v)}
            >
              {itemsOpen ? 'Hide items' : 'View items'}
            </button>
          </div>

          {itemsOpen && (
            <div className="job-items-panel">
              {itemsLoading ? (
                <p className="muted">Loading items…</p>
              ) : displayItems.length === 0 ? (
                <p className="muted">No job items.</p>
              ) : (
                <div className="table-scroll">
                  <table className="data-table job-items-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Attempts</th>
                        <th>Worker</th>
                        <th>Error</th>
                        <th>Started</th>
                        <th>Finished</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <span title={item.item_key || item.id}>{formatJobItemSource(item)}</span>
                            <span className="muted" title={item.id}>
                              {' '}
                              ({shortId(item.id)})
                            </span>
                          </td>
                          <td>
                            <StatusBadge variant={jobStatusVariant(item.status)}>
                              {jobStatusLabel(item.status)}
                            </StatusBadge>
                          </td>
                          <td>
                            {item.attempt_count}/{item.max_attempts}
                          </td>
                          <td>{item.lease_owner || '—'}</td>
                          <td>{formatTableError(item.error)}</td>
                          <td>{formatTime(item.started_at)}</td>
                          <td>{formatTime(item.finished_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
